import { useState, useEffect } from 'react';
import { Target, CheckCircle2, Clock, Pause, RefreshCw } from 'lucide-react';
import { getGoals, updateGoalProgress } from '../lib/api';
import type { Goal } from '../lib/types';

const statusConfig = {
  active: { icon: Target, color: 'text-brain-400', label: 'Active' },
  completed: { icon: CheckCircle2, color: 'text-green-400', label: 'Completed' },
  paused: { icon: Pause, color: 'text-yellow-400', label: 'Paused' },
};

const categoryColors = {
  quarterly: 'bg-purple-500/20 text-purple-400',
  annual: 'bg-blue-500/20 text-blue-400',
  ongoing: 'bg-green-500/20 text-green-400',
};

export function GoalProgress() {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadGoals = async () => {
    setIsLoading(true);
    setError(null);

    const response = await getGoals();

    if (response.success && response.data) {
      setGoals(response.data.goals);
    } else {
      setError(response.error || 'Failed to load goals');
    }

    setIsLoading(false);
  };

  useEffect(() => {
    loadGoals();
  }, []);

  const handleProgressUpdate = async (goalId: string, progress: number) => {
    const response = await updateGoalProgress(goalId, progress);

    if (response.success && response.data) {
      setGoals((prev) =>
        prev.map((g) => (g.id === goalId ? response.data!.goal : g))
      );
    }

    setEditingId(null);
  };

  const activeGoals = goals.filter((g) => g.status === 'active');
  const completedGoals = goals.filter((g) => g.status === 'completed');
  const pausedGoals = goals.filter((g) => g.status === 'paused');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Goals</h2>
        <button
          onClick={loadGoals}
          disabled={isLoading}
          className="p-2 text-gray-400 hover:text-gray-200 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="card border-red-900 bg-red-950/50">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {!isLoading && goals.length === 0 && (
        <div className="card text-center py-8">
          <Target className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-500">No goals defined yet</p>
        </div>
      )}

      {/* Active Goals */}
      {activeGoals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide">
            Active ({activeGoals.length})
          </h3>
          {activeGoals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              isEditing={editingId === goal.id}
              onEdit={() => setEditingId(goal.id)}
              onProgressUpdate={handleProgressUpdate}
            />
          ))}
        </div>
      )}

      {/* Paused Goals */}
      {pausedGoals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide">
            Paused ({pausedGoals.length})
          </h3>
          {pausedGoals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              isEditing={editingId === goal.id}
              onEdit={() => setEditingId(goal.id)}
              onProgressUpdate={handleProgressUpdate}
            />
          ))}
        </div>
      )}

      {/* Completed Goals */}
      {completedGoals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide">
            Completed ({completedGoals.length})
          </h3>
          {completedGoals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} isEditing={false} />
          ))}
        </div>
      )}
    </div>
  );
}

interface GoalCardProps {
  goal: Goal;
  isEditing: boolean;
  onEdit?: () => void;
  onProgressUpdate?: (goalId: string, progress: number) => void;
}

function GoalCard({ goal, isEditing, onEdit, onProgressUpdate }: GoalCardProps) {
  const [localProgress, setLocalProgress] = useState(goal.progress);
  const config = statusConfig[goal.status];
  const Icon = config.icon;

  return (
    <div className="card">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${config.color} mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-medium">{goal.title}</p>
              <p className="text-sm text-gray-400 mt-1">{goal.description}</p>
            </div>
            <span
              className={`text-xs px-2 py-1 rounded ${categoryColors[goal.category]}`}
            >
              {goal.category}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-400">Progress</span>
              <span className="text-gray-300">{Math.round(goal.progress)}%</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-brain-500 transition-all duration-300"
                style={{ width: `${goal.progress}%` }}
              />
            </div>
          </div>

          {/* Edit progress */}
          {isEditing && onProgressUpdate && (
            <div className="mt-3 flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={localProgress}
                onChange={(e) => setLocalProgress(Number(e.target.value))}
                className="flex-1"
              />
              <button
                onClick={() => onProgressUpdate(goal.id, localProgress)}
                className="btn-primary text-sm py-1 px-3"
              >
                Save
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              {goal.target_date && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(goal.target_date).toLocaleDateString()}
                </span>
              )}
            </div>
            {goal.status === 'active' && onEdit && (
              <button
                onClick={onEdit}
                className="text-brain-400 hover:text-brain-300"
              >
                Update progress
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
